/**
 * Skin analysis API client.
 * Centralizes detect-skin and condition-descriptions requests.
 */

import { getApiBaseUrl } from './apiConfig';
import { fetchWithTimeout } from './fetchWithTimeout';
import { fetchJsonWithRetry } from './fetchWithRetry';
import { TIMEOUT_MS } from './skinResilience';
import { compressDetectImages } from './imageCompress';

const DETECT_SKIN_PATH = '/api/detect-skin';
const DESCRIPTIONS_PATH = '/api/generate-condition-descriptions';
const STORED_DESCRIPTIONS_PATH = '/api/get-stored-condition-descriptions';
const GENERATE_MASK_PATH = '/api/generate-skin-condition-mask';

const DETECT_PROGRESS_STEP_MS = 2600;

const SINGLE_DETECT_PROGRESS_STEPS = [
  { phase: 'upload', message: 'Uploading your photo securely…' },
  { phase: 'scan', message: 'Scanning facial features…' },
  { phase: 'detect', message: 'Detecting skin conditions…' },
  { phase: 'score', message: 'Calculating your skin score…' },
  { phase: 'finalize', message: 'Preparing your results…' },
];

const BATCH_DETECT_PROGRESS_STEPS = [
  { phase: 'upload', message: 'Uploading your captures…' },
  { phase: 'scan', message: 'Processing front and side views…' },
  { phase: 'detect', message: 'Detecting conditions across all views…' },
  { phase: 'score', message: 'Aggregating your skin score…' },
  { phase: 'finalize', message: 'Preparing your personalized report…' },
];

function startDetectProgressTicker(onProgress, { batchSize, isBatch }) {
  if (typeof onProgress !== 'function') {
    return { stop() {}, pause() {}, resume() {} };
  }

  const steps = isBatch ? BATCH_DETECT_PROGRESS_STEPS : SINGLE_DETECT_PROGRESS_STEPS;
  let stepIndex = 0;
  let paused = false;
  let resumeTimerId = null;

  const emitStep = () => {
    if (paused) return;
    const step = steps[stepIndex];
    onProgress({
      phase: step.phase,
      message: step.message,
      batchSize,
      viewsCompleted: 0,
      stepIndex,
      stepCount: steps.length,
    });
  };

  emitStep();

  const intervalId = setInterval(() => {
    if (paused || stepIndex >= steps.length - 1) return;
    stepIndex += 1;
    emitStep();
  }, DETECT_PROGRESS_STEP_MS);

  return {
    stop: () => {
      if (resumeTimerId) clearTimeout(resumeTimerId);
      clearInterval(intervalId);
    },
    pause: () => {
      paused = true;
    },
    resume: () => {
      paused = false;
    },
    resumeAfter: (delayMs) => {
      if (resumeTimerId) clearTimeout(resumeTimerId);
      resumeTimerId = setTimeout(() => {
        resumeTimerId = null;
        paused = false;
      }, delayMs);
    },
  };
}

function normalizeMaskConditionInput(condition) {
  const raw = String(condition || '').trim();
  if (!raw) return raw;
  const lower = raw.toLowerCase();
  const compact = lower.replace(/[\s_-]+/g, '');

  if (compact === 'unevenskin' || compact === 'unevenskintone' || compact === 'uneventone' || compact === 'eventone') {
    return 'uneven_skintone';
  }
  if (compact === 'hydration') return 'hydration';
  if (compact === 'oxygen') return 'oxygen';
  if (compact === 'smoothness') return 'smoothness';
  if (compact === 'dullness') return 'dullness';

  return raw;
}

/**
 * @returns {string} Normalized API base URL (no trailing slash).
 */
function getBaseUrl() {
  const raw = getApiBaseUrl();
  return (raw || '').replace(/\/$/, '');
}

/**
 * Build full URL for an API path.
 * @param {string} path - e.g. '/api/detect-skin'
 * @returns {string}
 */
export function buildSkinAnalysisApiUrl(path) {
  const base = getBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

/**
 * Run skin detection on an image.
 *
 * @param {object} params
 * @param {string} params.image - Data URL of the image
 * @param {string} [params.sessionId]
 * @param {number|null} [params.age]
 * @param {string[]|null} [params.allowedConditions]
 * @param {string|null} [params.appSessionToken]
 * @returns {Promise<{ success: boolean, session_id?: string, marked_image?: string, detections?: unknown[], skin_conditions?: object, skin_score?: number, error?: string }>}
 */
export async function detectSkin({ image, sessionId, age, allowedConditions, appSessionToken, masksPolicy, landmarks, captureView }) {
  return detectSkinBatch({
    frontImage: image,
    sessionId,
    age,
    allowedConditions,
    appSessionToken,
    masksPolicy,
    landmarks,
    captureView,
  });
}

/**
 * Run batched skin detection (front + optional side captures in one request).
 * Uses a single Redis lock lifecycle on the backend.
 */
export async function detectSkinBatch({
  frontImage,
  sideCaptures,
  sessionId,
  age,
  allowedConditions,
  appSessionToken,
  landmarks,
  masksPolicy,
  captureView,
  retryCount = 0,
  onProgress,
  signal,
  onRetry,
}) {
  const front = frontImage || null;
  if (!front) {
    throw new Error('Front image is required');
  }

  const compressed = await compressDetectImages(front, sideCaptures);
  const side_captures = {};
  if (compressed.sideCaptures?.left) side_captures.left = compressed.sideCaptures.left;
  if (compressed.sideCaptures?.right) side_captures.right = compressed.sideCaptures.right;
  const sideCount = Object.keys(side_captures).length;
  const batchSize = 1 + sideCount;
  const progressTicker = startDetectProgressTicker(onProgress, {
    batchSize,
    isBatch: sideCount > 0,
  });

  const handleRetry = (info) => {
    progressTicker.pause();
    if (typeof onRetry === 'function') {
      onRetry(info);
    }
    progressTicker.resumeAfter(Number(info?.delayMs) || 0);
  };

  const url = buildSkinAnalysisApiUrl(DETECT_SKIN_PATH);
  const body = {
    image: compressed.frontImage,
    front_image: compressed.frontImage,
    conf_threshold: 0.06,
    session_id: sessionId ?? null,
    age: age ?? null,
    batch: sideCount > 0,
    retry_count: retryCount,
    ...(sideCount > 0 && { side_captures }),
    ...(side_captures.left && { left_image: side_captures.left }),
    ...(side_captures.right && { right_image: side_captures.right }),
    ...(allowedConditions && allowedConditions.length > 0 && { allowed_conditions: allowedConditions }),
    ...(appSessionToken && { app_session_token: appSessionToken }),
    ...(masksPolicy && { masks_policy: masksPolicy }),
    ...(landmarks && { landmarks }),
    ...(captureView && { capture_view: captureView }),
  };

  try {
    const data = await fetchJsonWithRetry(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        timeoutMs: sideCount > 0 ? TIMEOUT_MS.BATCH_DETECT : TIMEOUT_MS.SINGLE_DETECT,
        signal,
      },
      { label: 'detect-skin-batch', onRetry: handleRetry }
    );

    if (typeof onProgress === 'function') {
      onProgress({
        phase: 'complete',
        message: 'Analysis complete',
        batchSize,
        viewsCompleted: batchSize,
      });
    }

    return data;
  } finally {
    progressTicker.stop();
  }
}

/**
 * @deprecated Use detectSkinBatch — kept as alias for existing imports.
 */
export async function detectSkinWithSideCaptures(params) {
  return detectSkinBatch(params);
}

/**
 * Fetch stored condition descriptions.
 *
 * @param {object} payload - Full payload for /api/generate-condition-descriptions
 * @returns {Promise<Record<string, string>>} Map of condition key -> description (empty on failure)
 */
export async function fetchConditionDescriptions(payload) {
  if (!payload) return {};
  const url = buildSkinAnalysisApiUrl(DESCRIPTIONS_PATH);
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    timeoutMs: 60000,
  });
  const data = await response.json().catch(() => ({}));
  if (!data.success || !data.descriptions || typeof data.descriptions !== 'object') {
    return {};
  }
  return Object.keys(data.descriptions).length > 0 ? data.descriptions : {};
}

/**
 * Fetch stored AI condition descriptions.
 *
 * @param {object} params
 * @param {string} params.sessionId
 * @param {string|null} [params.appSessionToken]
 * @param {boolean} [params.generateIfMissing=false]
 * @param {string[]} [params.requestedConditions]
 * @returns {Promise<Record<string, string>>}
 */
export async function fetchStoredConditionDescriptions({
  sessionId,
  appSessionToken,
  generateIfMissing = false,
  requestedConditions,
}) {
  if (!sessionId) return {};
  const url = new URL(buildSkinAnalysisApiUrl(STORED_DESCRIPTIONS_PATH));
  url.searchParams.set('session_id', sessionId);
  if (appSessionToken) url.searchParams.set('app_session_token', appSessionToken);
  url.searchParams.set('generate_if_missing', generateIfMissing ? 'true' : 'false');
  if (Array.isArray(requestedConditions) && requestedConditions.length > 0) {
    url.searchParams.set('requested_conditions', requestedConditions.join(','));
  }
  const response = await fetchWithTimeout(url.toString(), { timeoutMs: 20000 });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success || !data.descriptions || typeof data.descriptions !== 'object') {
    return {};
  }
  return Object.keys(data.descriptions).length > 0 ? data.descriptions : {};
}

/**
 * Lazily generate a single skin concern mask for a session.
 *
 * @param {object} params
 * @param {string} params.sessionId
 * @param {string} params.condition - Display name ("Hydration") or backend key ("hydration")
 * @param {string|null} [params.appSessionToken]
 * @returns {Promise<{ success: boolean, condition_key?: string, mask?: string | null, error?: string }>}
 */
export async function generateSkinConditionMask({ sessionId, condition, appSessionToken }) {
  const url = buildSkinAnalysisApiUrl(GENERATE_MASK_PATH);
  const normalizedCondition = normalizeMaskConditionInput(condition);
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      session_id: sessionId,
      condition: normalizedCondition,
      ...(appSessionToken && { app_session_token: appSessionToken }),
    }),
    timeoutMs: 60000,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || response.statusText || 'Mask request failed');
    error.status = response.status;
    error.apiResponse = data;
    throw error;
  }
  return data;
}